/**
 * Auth token storage + Zustand store.
 *
 * Design notes:
 * - JWT is kept in localStorage under a namespaced key so customer and admin
 *   builds don't collide. We do NOT put it in cookies — the backend is a
 *   bearer-token API, so localStorage is appropriate and simpler than CSRF.
 * - `useAuth` is the React hook. Non-React code (api.ts) uses `getToken()`.
 */
import { create } from "zustand";

const TOKEN_KEY = "warnix_v2_token";
const USER_KEY = "warnix_v2_user";

export type UserRole = "admin" | "customer" | "viewer";

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  site_id: string | null;
  /** UI language (one of LANGUAGE_CODES). Mirrors v2_users.lang and the
   *  `lang` JWT claim — the backend uses it to pick the push/summary
   *  language, the frontend to restore the choice on a new device. */
  lang?: string;
}

// -------- vanilla helpers (used by api.ts) --------

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Decode the JWT `exp` claim (unix seconds). Returns null if the token is
 * malformed or missing — the caller should treat null as "no hint, fall
 * through to the usual 401 handling."
 */
export function getTokenExpiry(token: string | null = getToken()): number | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    const exp = payload?.exp;
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
}

function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function clearAuth(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // ignore
  }
  useAuth.setState({ token: null, user: null });
}

// -------- zustand store --------

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setSession: (token: string, user: AuthUser) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  token: getToken(),
  user: getStoredUser(),

  setSession: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    set({ token, user });
  },

  logout: () => {
    clearAuth();
  },

  isAuthenticated: () => Boolean(get().token && get().user),
}));
